/**
 * PolarProgressChart — the "Charted Atlas" polar-area chart for the Discovery
 * Progress tab. Every topic's full slice always exists as a visible pale
 * "mist" sector (the territory still to chart); the topic's understanding is
 * a solid pie sector in its full identity hue (topicPalette) — depth is
 * encoded by radius alone, through the shared `depthRadiusFrac` bias curve —
 * all over dashed guide rings at ⅓ and ⅔. A thin identity rim in the topic's
 * hue sits at each slice's inner edge (the seed its wedge grows out of), so
 * every slice is keyed to its row at any progress; an invisible topmost hit
 * layer makes the whole slice (mist, wedge, rim) the pointer surface. A white center disc hides the sector tips; a
 * zero-progress topic is pure mist. The chart carries no labels — identity
 * lives in the topic rows beside it (hover a slice ↔ hover a row), which
 * also keeps every slice legible as claimable territory at any progress
 * mix. The center disc shows the overall percentage when `centerPct` is
 * passed (the Progress tab does, matching the marketing chart; the right
 * panel omits it and keeps an empty disc). On hover-capable pointers a
 * cursor-following tooltip names the hovered slice with its honest band
 * word and percentage (topics need a `label` for this).
 *
 * Follows the `BrainViz` class precedent (constructor/build/destroy,
 * DOM-API SVG construction, zero framework dependency) and its
 * accessibility posture: the chart SVG is `aria-hidden="true"` and wedges
 * carry no `tabindex`/`role`/`aria-label` — the topic rows below the chart
 * are the sole keyboard path into this data.
 *
 *   import PolarProgressChart from '@/components/PolarProgressChart.js';
 *   const chart = new PolarProgressChart('[data-polar-chart]', {
 *     topics: [{ id: 'sex', progress: 20 }, ...],
 *     onWedgeHover: (topicId) => { ... sync row hot state ... },
 *     onWedgeClick: (topicId) => { ... navigate to topic detail ... }
 *   });
 *   new PolarProgressChart(el, { topics, interactive: false });  // display-only
 *
 *   chart.spotlight('sex');   // dim every other wedge, highlight this one
 *   chart.spotlight(null);    // clear
 *   chart.destroy();
 */

import { topicFill } from './topicPalette.js';
import { levelLabel } from './depthProgress.js';

const NS = 'http://www.w3.org/2000/svg';

// Geometry constants. The viewBox hugs the mist sectors (no label margin, no
// plate): wedges max out at WEDGE_MAX; CENTER_DISC is the white disc a wedge
// grows from (progress interpolates CENTER_DISC→WEDGE_MAX). Stroke widths in
// features/discovery.css are in these units (~1 unit ≈ 1px at the 440px
// desktop render).
const VIEWBOX = '0 0 420 420';
const CX = 210;
const CY = 210;
const WEDGE_MAX = 198;
// 52/198 ≈ the studio prototype's hole-to-radius ratio (13/50).
const CENTER_DISC = 52;
// Angular gap between slices — the studio prototype's value.
const WEDGE_GAP_DEG = 3.4;
// Identity rim: a thin arc of the topic's hue at the slice's inner edge —
// the seed its wedge grows out of, so every slice is keyed to its row at
// ANY progress and merges seamlessly into the wedge once one exists.
const ID_ARC_INNER = CENTER_DISC;
const ID_ARC_OUTER = CENTER_DISC + 7;
// Pointer surface extent (independent of the rim's placement).
const HIT_OUTER = WEDGE_MAX + 8;

// Entrance animation timing: wedges grow in clockwise-staggered (the CSS
// transition + springy bezier live in features/discovery.css). Mirrors the
// marketing chart's reveal; the mist ground appears instantly beneath them.
const ENTER_BASE_DELAY_MS = 120;
const ENTER_STAGGER_MS = 60;
// Center-percent count-up duration — same value as the marketing chart's
// count-up, ending as the last wedges settle.
const COUNT_MS = 1300;

/**
 * Clamp a progress value to [0, 100], coercing non-finite input to 0.
 * @param {number} v - Raw value
 * @returns {number} Clamped 0-100 value
 * @private
 */
function clamp(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/**
 * Convert a (radius, angle) pair into an [x, y] point around (cx, cy), with
 * 0deg pointing straight up (12 o'clock) — matches the mockup's convention.
 * @param {number} cx
 * @param {number} cy
 * @param {number} r
 * @param {number} aDeg - Angle in degrees, 0 = 12 o'clock, clockwise
 * @returns {[number, number]}
 */
export function polar(cx, cy, r, aDeg) {
  const a = ((aDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

/**
 * Build an SVG path `d` string for an annular sector ("donut wedge") between
 * inner radius r0 and outer radius r1, spanning angles a0..a1 (degrees).
 * @param {number} cx
 * @param {number} cy
 * @param {number} r0 - Inner radius
 * @param {number} r1 - Outer radius
 * @param {number} a0 - Start angle (degrees)
 * @param {number} a1 - End angle (degrees)
 * @returns {string} SVG path `d` attribute value
 */
export function wedgePath(cx, cy, r0, r1, a0, a1) {
  const large = a1 - a0 > 180 ? 1 : 0;
  const p1 = polar(cx, cy, r1, a0);
  const p2 = polar(cx, cy, r1, a1);
  const p3 = polar(cx, cy, r0, a1);
  const p4 = polar(cx, cy, r0, a0);
  return (
    `M${p1[0].toFixed(2)} ${p1[1].toFixed(2)} ` +
    `A${r1} ${r1} 0 ${large} 1 ${p2[0].toFixed(2)} ${p2[1].toFixed(2)} ` +
    `L${p3[0].toFixed(2)} ${p3[1].toFixed(2)} ` +
    `A${r0} ${r0} 0 ${large} 0 ${p4[0].toFixed(2)} ${p4[1].toFixed(2)} Z`
  );
}

// Depth→radius bias: early progress is deliberately amplified (a 3% write
// renders at ~9% of the band, 8% at ~17%) so the first sessions visibly move
// the map; 100% still lands exactly full. Chosen over linear by Doug —
// visual impact outranks gauge accuracy here (the true numbers live in the
// center %, the KPI tiles and the topic detail).
const DEPTH_BIAS_EXP = 0.7;

/**
 * Map a 0–1 depth fraction to its 0–1 radius fraction along the wedge band —
 * the ONE bias curve every Charted Atlas surface must share (the mini polar
 * map imports this), so a section-close write lands at exactly the radius
 * the Progress chart draws. Input is clamped to [0, 1].
 * @param {number} frac - Depth, 0–1
 * @returns {number} Radius fraction, 0–1
 */
export function depthRadiusFrac(frac) {
  return Math.pow(Math.max(0, Math.min(1, frac)), DEPTH_BIAS_EXP);
}

/**
 * Outer radius of a topic's wedge at a given progress (0–100); a
 * zero-progress topic draws no wedge, so its "outer edge" is the disc.
 * @param {number} progress - Depth 0–100 (already clamped by callers)
 * @returns {number} Radius in viewBox units
 * @private
 */
function wedgeOuterRadius(progress) {
  if (progress <= 0) return CENTER_DISC;
  return CENTER_DISC + (WEDGE_MAX - CENTER_DISC) * depthRadiusFrac(progress / 100);
}

/**
 * Build an SVG path `d` string for a solid pie sector from the center point
 * out to radius r, spanning angles a0..a1 (degrees). The chart's wedges and
 * territory hit-surfaces are sectors; `wedgePath` (annular) remains for the
 * mini polar map's ring-growth animation.
 * @param {number} cx
 * @param {number} cy
 * @param {number} r - Outer radius
 * @param {number} a0 - Start angle (degrees)
 * @param {number} a1 - End angle (degrees)
 * @returns {string} SVG path `d` attribute value
 */
export function sectorPath(cx, cy, r, a0, a1) {
  const large = a1 - a0 > 180 ? 1 : 0;
  const p1 = polar(cx, cy, r, a0);
  const p2 = polar(cx, cy, r, a1);
  return (
    `M${cx} ${cy} ` +
    `L${p1[0].toFixed(2)} ${p1[1].toFixed(2)} ` +
    `A${r} ${r} 0 ${large} 1 ${p2[0].toFixed(2)} ${p2[1].toFixed(2)} Z`
  );
}

/**
 * Create and (optionally) append a namespaced SVG element with attributes.
 * @param {string} tag - SVG tag name
 * @param {Object} [attrs] - Attribute map
 * @param {SVGElement} [parent] - Optional parent to append to
 * @returns {SVGElement}
 * @private
 */
function svgEl(tag, attrs = {}, parent) {
  const el = document.createElementNS(NS, tag);
  for (const k in attrs) {
    if (Object.hasOwn(attrs, k)) el.setAttribute(k, attrs[k]);
  }
  if (parent) parent.appendChild(el);
  return el;
}

export class PolarProgressChart {
  /**
   * @param {string|Element} mountEl - Selector or element to mount the chart into
   * @param {Object} opts
   * @param {Array<{id: string, progress: number, label?: string}>} opts.topics - Topics
   *   in display order; `label` feeds the hover tooltip (falls back to the id)
   * @param {(topicId: string|null) => void} [opts.onWedgeHover] - Fired on pointer-originated wedge hover/unhover (hover-capable pointers only)
   * @param {(topicId: string) => void} [opts.onWedgeClick] - Fired on wedge click/tap
   * @param {boolean} [opts.interactive=true] - When false the chart is a pure
   *   display surface: no hover/click bindings at all and a `polar-chart--static`
   *   class that resets the territories' pointer cursor. Omitting the two
   *   callbacks alone is not enough — the bindings still repaint the spotlight
   *   on hover and the slices still read as tappable.
   * @param {number|null} [opts.centerPct=null] - Overall percentage shown in
   *   the center disc. Omit/null keeps the disc empty. Rounded for display;
   *   counts up with the entrance animation and always lands on the true
   *   value (honest destination).
   * @param {boolean} [opts.animate=true] - Play the entrance on mount: wedges
   *   grow from the center clockwise-staggered (CSS transition released one
   *   frame after build) while the center percent counts up. Skipped under a
   *   reduced-motion preference — and wherever `matchMedia` is unavailable,
   *   since motion that can't be confirmed acceptable is not played.
   */
  constructor(mountEl, opts = {}) {
    this.mountEl = typeof mountEl === 'string' ? document.querySelector(mountEl) : mountEl;
    if (!this.mountEl) throw new Error('PolarProgressChart: mount element not found');
    if (!Array.isArray(opts.topics)) throw new Error('PolarProgressChart: opts.topics array is required');

    this.opts = Object.assign(
      {
        topics: [],
        onWedgeHover: null,
        onWedgeClick: null,
        interactive: true,
        centerPct: null,
        animate: true
      },
      opts
    );

    // Entrance gate: play motion only when the environment can confirm it is
    // acceptable — a reduced-motion preference or a missing matchMedia both
    // keep the chart static.
    this._motionOK = (() => {
      try {
        return !matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch {
        return false;
      }
    })();

    // Pointer-gate: bind wedge hover/spotlight only on hover-capable pointers.
    // An unguarded synthesized pointerenter on tap would repaint the chart
    // and make iOS Safari swallow the first tap (see sessions.css / the
    // documented content-row touch trap) — click stays bound unconditionally
    // below so tap-to-navigate always fires on the first tap.
    this._hoverCapable = (() => {
      try {
        return matchMedia('(hover: hover)').matches;
      } catch {
        return false;
      }
    })();

    this._wedgeEls = new Map();
    this._territoryEls = new Map();
    this._arcEls = new Map();
    this._countRaf = null;
    this._build();
  }

  // ---- public API -----------------------------------------------------

  /**
   * Highlight one wedge (dimming the rest), or clear the spotlight. Used to
   * sync the chart to an externally-hovered topic row. Does NOT fire
   * `onWedgeHover` — that callback only fires for pointer-originated wedge
   * hover, so wiring `row hover -> chart.spotlight()` and
   * `chart wedge hover -> onWedgeHover -> row hot class` never echoes.
   * @param {string|null} [topicId] - Topic id to spotlight, or null to clear
   */
  spotlight(topicId = null) {
    this._applySpotlight(topicId);
  }

  /** Tear down the chart and release its DOM. Safe to call multiple times. */
  destroy() {
    if (!this.mountEl) return;
    if (this._countRaf) cancelAnimationFrame(this._countRaf);
    this._countRaf = null;
    this.mountEl.innerHTML = '';
    this._wedgeEls.clear();
    this._territoryEls.clear();
    this._arcEls.clear();
    this._tip = null;
    this.svg = null;
    this.mountEl = null;
  }

  // ---- internals --------------------------------------------------------

  /**
   * Count the center number 0 → target alongside the wedge entrance — same
   * duration and ease-out cubic as the marketing chart's count-up, always
   * finishing exactly on the true value.
   * @param {Text} numNode - The text node holding the number
   * @param {number} target - Final integer percentage
   * @private
   */
  _countUp(numNode, target) {
    const t0 = performance.now();
    const tick = (now) => {
      const f = Math.min(1, (now - t0) / COUNT_MS);
      const eased = 1 - Math.pow(1 - f, 3);
      numNode.nodeValue = String(Math.round(eased * target));
      this._countRaf = f < 1 ? requestAnimationFrame(tick) : null;
    };
    this._countRaf = requestAnimationFrame(tick);
  }

  /**
   * Fill and show the hover tooltip for a topic.
   * @param {{id: string, progress: number, label?: string}} t
   * @private
   */
  _showTip(t) {
    if (!this._tip) return;
    const pct = Math.round(clamp(t.progress));
    this._tip.textContent = t.label || t.id;
    const meta = document.createElement('span');
    meta.className = 'polar-tip__meta';
    meta.textContent = `${levelLabel(pct)} · ${pct}%`;
    this._tip.appendChild(meta);
    this._tip.classList.add('polar-tip--show');
  }

  /**
   * Follow the pointer, offset so the pill never sits under the cursor.
   * @param {PointerEvent} e
   * @private
   */
  _moveTip(e) {
    if (!this._tip || !this.mountEl) return;
    const rect = this.mountEl.getBoundingClientRect();
    this._tip.style.left = `${e.clientX - rect.left + 14}px`;
    this._tip.style.top = `${e.clientY - rect.top - 10}px`;
  }

  /** @private */
  _hideTip() {
    if (this._tip) this._tip.classList.remove('polar-tip--show');
  }

  /**
   * @param {string|null} topicId
   * @private
   */
  _applySpotlight(topicId) {
    if (!this.svg) return;
    const dimming = topicId != null;
    this.svg.classList.toggle('dimming', dimming);
    this._wedgeEls.forEach((el, id) => {
      el.classList.toggle('hot', dimming && id === topicId);
    });
    this._territoryEls.forEach((el, id) => {
      el.classList.toggle('hot', dimming && id === topicId);
    });
    this._arcEls.forEach((el, id) => {
      el.classList.toggle('hot', dimming && id === topicId);
    });
  }

  /** @private */
  _build() {
    const topics = this.opts.topics;
    const n = topics.length;
    const slice = n > 0 ? 360 / n : 0;
    const cx = CX;
    const cy = CY;

    const interactive = this.opts.interactive !== false;
    const entering = this.opts.animate !== false && this._motionOK;

    const svg = svgEl('svg', {
      viewBox: VIEWBOX,
      class: interactive ? 'polar-chart' : 'polar-chart polar-chart--static',
      'aria-hidden': 'true'
    });

    this._wedgeEls.clear();
    this._territoryEls.clear();

    // Territories — one visible mist sector per topic, under the wedges: the
    // pale "territory still to chart" that keeps every slice a complete part
    // of the picture at any progress. The mist fill lives in CSS
    // (.polar-territory — lockstep with MIST_FILL in topicPalette.js); the
    // white gaps between sectors do the old spokes' job. Each slice also
    // carries its identity rim — a thin arc of the topic's full hue just
    // outside the mist, keying the slice to its row at any progress.
    // Pointer interaction lives on the invisible hit layer built last.
    topics.forEach((t, i) => {
      const a0 = i * slice + WEDGE_GAP_DEG / 2;
      const a1 = (i + 1) * slice - WEDGE_GAP_DEG / 2;
      const territory = svgEl(
        'path',
        {
          d: sectorPath(cx, cy, WEDGE_MAX, a0, a1),
          class: 'polar-territory',
          'data-id': t.id
        },
        svg
      );
      this._territoryEls.set(t.id, territory);

      // The rim occupies only the band the wedge does NOT cover — rim and
      // wedge never stack, so the spotlight's opacity fade cannot compound
      // where they overlap into a darker inner arc (same-hue translucent
      // layers double-paint). A wedge past the rim band retires its rim.
      const rimInner = Math.min(Math.max(wedgeOuterRadius(clamp(t.progress)), ID_ARC_INNER), ID_ARC_OUTER);
      if (rimInner < ID_ARC_OUTER) {
        const arc = svgEl(
          'path',
          {
            d: wedgePath(cx, cy, rimInner, ID_ARC_OUTER, a0, a1),
            fill: topicFill(t.id),
            class: 'polar-idarc',
            'data-id': t.id
          },
          svg
        );
        this._arcEls.set(t.id, arc);
      }
    });

    // Dashed guide rings at ⅓ and ⅔ of the wedge band — the studio
    // prototype's rings (#e2e8ee, 0.3 stroke, 0.8/1.2 dash on its R=50
    // canvas), scaled by our 198/50 radius ratio. Drawn over the mist
    // (which is opaque) but under the wedges.
    [1 / 3, 2 / 3].forEach((f) => {
      svgEl(
        'circle',
        {
          cx,
          cy,
          r: CENTER_DISC + (WEDGE_MAX - CENTER_DISC) * f,
          fill: 'none',
          stroke: '#e2e8ee',
          'stroke-width': 1.2,
          'stroke-dasharray': '3.2 4.8',
          class: 'polar-grid polar-ring polar-ring--dashed'
        },
        svg
      );
    });

    // Wedges — the charted understanding: the topic's full identity hue
    // (topicPalette), depth encoded by radius alone. Purely visual — the
    // territories above carry all pointer interaction for the full slice.
    // A zero-progress topic draws no wedge (its slice stays pure mist).
    topics.forEach((t, i) => {
      const progress = clamp(t.progress);
      const a0 = i * slice + WEDGE_GAP_DEG / 2;
      const a1 = (i + 1) * slice - WEDGE_GAP_DEG / 2;

      if (progress > 0) {
        const r1 = wedgeOuterRadius(progress);
        const wedge = svgEl(
          'path',
          {
            d: sectorPath(cx, cy, r1, a0, a1),
            fill: topicFill(t.id),
            class: 'polar-wedge',
            'data-id': t.id
          },
          svg
        );
        if (entering) {
          // Scaling about the chart center is equivalent to animating the
          // radius for a center-anchored sector; the delay staggers the
          // wedges clockwise (consumed by the transform transition only —
          // see .polar-wedge in features/discovery.css).
          wedge.style.transformBox = 'view-box';
          wedge.style.transformOrigin = `${cx}px ${cy}px`;
          wedge.style.setProperty('--wedge-delay', `${ENTER_BASE_DELAY_MS + i * ENTER_STAGGER_MS}ms`);
        }
        this._wedgeEls.set(t.id, wedge);
      }
    });

    // Center disc — drawn after the wedges so it covers the sector tips at
    // any progress mix, and keeps the ambiguous center area pointer-neutral.
    // It carries the overall percentage when `centerPct` is provided (the SVG
    // is aria-hidden — the band word in the KPI strip stays the accessible
    // rendering of the overall number).
    svgEl('circle', { cx, cy, r: CENTER_DISC, fill: '#ffffff', stroke: '#eef2f6', 'stroke-width': 1.25, class: 'polar-centerdisc' }, svg);

    const centerPct = Number(this.opts.centerPct);
    if (this.opts.centerPct != null && Number.isFinite(centerPct)) {
      const target = Math.round(clamp(centerPct));
      const text = svgEl(
        'text',
        {
          x: cx,
          y: cy + 2,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
          class: 'polar-centerpct'
        },
        svg
      );
      // Number text node first, then the smaller % unit — mirrors the
      // marketing chart's markup so the count-up can rewrite just the number.
      const numNode = document.createTextNode(String(entering ? 0 : target));
      text.appendChild(numNode);
      svgEl('tspan', { class: 'polar-centerpct__unit' }, text).textContent = '%';
      if (entering) this._countUp(numNode, target);
    }

    // Hover tooltip — the dark cursor-following pill from the Charted Atlas
    // studio: topic label plus the honest band word · percentage (the
    // radius is bias-curved; the tooltip is where the true number lives on
    // the chart). Hover-capable pointers only, and aria-hidden like the
    // rest of the chart — the rows stay the accessible rendering.
    let tip = null;
    if (interactive && this._hoverCapable) {
      tip = document.createElement('div');
      tip.className = 'polar-tip';
      tip.setAttribute('aria-hidden', 'true');
    }
    this._tip = tip;

    // Hit layer — one invisible annular sector per slice, topmost, spanning
    // from the center disc's edge out past the mist: hover/click anywhere
    // in a slice (mist, wedge or rim — never the neutral center) spotlights
    // and opens that topic. A static chart builds no hit layer at all.
    if (interactive) {
      topics.forEach((t, i) => {
        const a0 = i * slice + WEDGE_GAP_DEG / 2;
        const a1 = (i + 1) * slice - WEDGE_GAP_DEG / 2;
        const hit = svgEl(
          'path',
          {
            d: wedgePath(cx, cy, CENTER_DISC, HIT_OUTER, a0, a1),
            fill: 'transparent',
            class: 'polar-hitzone',
            'data-id': t.id
          },
          svg
        );
        // Pointer-gate: wedge hover binds only on hover-capable pointers —
        // an unguarded synthesized pointerenter on tap would repaint the
        // chart and make iOS Safari swallow the first tap (see sessions.css
        // and the documented content-row touch trap). Click stays bound
        // unconditionally so tap-to-navigate always fires on the first tap.
        if (this._hoverCapable) {
          hit.addEventListener('pointerenter', () => {
            this._applySpotlight(t.id);
            this._showTip(t);
            if (this.opts.onWedgeHover) this.opts.onWedgeHover(t.id);
          });
          hit.addEventListener('pointermove', (e) => this._moveTip(e));
          hit.addEventListener('pointerleave', () => {
            this._applySpotlight(null);
            this._hideTip();
            if (this.opts.onWedgeHover) this.opts.onWedgeHover(null);
          });
        }
        hit.addEventListener('click', () => {
          if (this.opts.onWedgeClick) this.opts.onWedgeClick(t.id);
        });
      });
    }

    this.mountEl.innerHTML = '';
    if (entering) svg.classList.add('polar-chart--enter');
    this.mountEl.appendChild(svg);
    if (tip) this.mountEl.appendChild(tip);
    this.svg = svg;

    if (entering) {
      // Two frames: the first paints the collapsed wedges, the second
      // releases them so the staggered transform transition runs. Guarded
      // against a rebuild/destroy landing between the frames.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.svg === svg) svg.classList.remove('polar-chart--enter');
        });
      });
    }
  }
}

export default PolarProgressChart;
